import { useState, useEffect } from 'react';
import { riskApi } from '../services/api';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Pagination } from '../components/ui/Pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/Table';
import {
  ShieldAlert,
  AlertTriangle,
  User,
  CheckCircle,
  XCircle,
  Settings,
  Ban,
} from 'lucide-react';

interface RiskAlert {
  id: string;
  type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  userId?: string;
  username?: string;
  description: string;
  detectedAt: string;
  status: 'open' | 'investigating' | 'resolved' | 'false_positive';
}

interface RiskSettings {
  maxAILCallsPerHour: number;
  maxWorkflowRunsPerHour: number;
  maxRegistrationsPerIP: number;
  blockedIPs: string[];
}

export function RiskPage() {
  const [alerts, setAlerts] = useState<RiskAlert[]>([]);
  const [settings, setSettings] = useState<RiskSettings | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [status, setStatus] = useState('');
  const [severity, setSeverity] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'alerts' | 'settings'>('alerts');
  const [newIP, setNewIP] = useState('');

  const fetchAlerts = async () => {
    setIsLoading(true);
    try {
      const response = await riskApi.listAlerts({
        page,
        pageSize,
        status: status || undefined,
        severity: severity || undefined,
      });
      if (response.code === 0) {
        setAlerts(response.data.items as RiskAlert[]);
        setTotal(response.data.total);
      }
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await riskApi.getRiskSettings();
      if (response.code === 0) {
        setSettings(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'alerts') {
      fetchAlerts();
    } else {
      fetchSettings();
    }
  }, [page, pageSize, status, severity, activeTab]);

  const handleUpdateAlertStatus = async (alertId: string, newStatus: string) => {
    try {
      await riskApi.updateAlertStatus(alertId, newStatus);
      fetchAlerts();
    } catch (err) {
      console.error('Failed to update alert status:', err);
    }
  };

  const handleUpdateSettings = async () => {
    if (!settings) return;
    try {
      await riskApi.updateRiskSettings(settings);
      alert('设置已保存');
    } catch (err) {
      console.error('Failed to update settings:', err);
    }
  };

  const handleAddBlockedIP = () => {
    if (!newIP || !settings) return;
    if (!settings.blockedIPs.includes(newIP)) {
      setSettings({
        ...settings,
        blockedIPs: [...settings.blockedIPs, newIP],
      });
      setNewIP('');
    }
  };

  const handleRemoveBlockedIP = (ip: string) => {
    if (!settings) return;
    setSettings({
      ...settings,
      blockedIPs: settings.blockedIPs.filter(i => i !== ip),
    });
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('zh-CN');
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <Badge variant="error">严重</Badge>;
      case 'high':
        return <Badge variant="error">高</Badge>;
      case 'medium':
        return <Badge variant="warning">中</Badge>;
      case 'low':
        return <Badge variant="info">低</Badge>;
      default:
        return <Badge>{severity}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return <Badge variant="error">待处理</Badge>;
      case 'investigating':
        return <Badge variant="warning">调查中</Badge>;
      case 'resolved':
        return <Badge variant="success">已解决</Badge>;
      case 'false_positive':
        return <Badge variant="default">误报</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getAlertTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      abnormal_ai_usage: '异常 AI 使用',
      high_frequency_workflow: '高频工作流运行',
      mass_registration: '批量注册',
      suspicious_ip: '可疑 IP',
    };
    return labels[type] || type;
  };

  const openCount = alerts.filter(a => a.status === 'open').length;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">风控系统</h1>
          <p className="text-zinc-500 mt-1">监控异常行为和风险</p>
        </div>
        {openCount > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 bg-error-50 text-error-600 rounded-lg">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-medium">{openCount} 个待处理告警</span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-zinc-200">
        <nav className="flex gap-6">
          {[
            { key: 'alerts', label: '风险告警', icon: ShieldAlert },
            { key: 'settings', label: '风控设置', icon: Settings },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as typeof activeTab)}
              className={`flex items-center gap-2 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-accent-600 text-accent-600'
                  : 'border-transparent text-zinc-500 hover:text-zinc-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'alerts' ? (
        <>
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-zinc-300 bg-white text-zinc-900"
                >
                  <option value="">所有状态</option>
                  <option value="open">待处理</option>
                  <option value="investigating">调查中</option>
                  <option value="resolved">已解决</option>
                  <option value="false_positive">误报</option>
                </select>
                <select
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-zinc-300 bg-white text-zinc-900"
                >
                  <option value="">所有级别</option>
                  <option value="critical">严重</option>
                  <option value="high">高</option>
                  <option value="medium">中</option>
                  <option value="low">低</option>
                </select>
              </div>
            </CardContent>
          </Card>

          {/* Alerts Table */}
          <Card>
            <CardHeader>
              <CardTitle>风险告警</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>级别</TableHead>
                        <TableHead>类型</TableHead>
                        <TableHead>用户</TableHead>
                        <TableHead>描述</TableHead>
                        <TableHead>检测时间</TableHead>
                        <TableHead>状态</TableHead>
                        <TableHead className="text-right">操作</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {alerts.map((alert) => (
                        <TableRow key={alert.id}>
                          <TableCell>{getSeverityBadge(alert.severity)}</TableCell>
                          <TableCell>
                            <span className="font-medium">{getAlertTypeLabel(alert.type)}</span>
                          </TableCell>
                          <TableCell>
                            {alert.username ? (
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-zinc-400" />
                                <span>{alert.username}</span>
                              </div>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-zinc-600">{alert.description}</span>
                          </TableCell>
                          <TableCell>{formatDate(alert.detectedAt)}</TableCell>
                          <TableCell>{getStatusBadge(alert.status)}</TableCell>
                          <TableCell className="text-right">
                            <select
                              value={alert.status}
                              onChange={(e) => handleUpdateAlertStatus(alert.id, e.target.value)}
                              className="text-sm border border-zinc-300 rounded-lg px-2 py-1"
                            >
                              <option value="open">待处理</option>
                              <option value="investigating">调查中</option>
                              <option value="resolved">已解决</option>
                              <option value="false_positive">误报</option>
                            </select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  <Pagination
                    currentPage={page}
                    totalPages={Math.ceil(total / pageSize)}
                    onPageChange={setPage}
                    totalItems={total}
                    pageSize={pageSize}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <>
          {/* Settings */}
          <Card>
            <CardHeader>
              <CardTitle>风控设置</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {settings && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-2">
                        每小时最大 AI 调用次数
                      </label>
                      <input
                        type="number"
                        value={settings.maxAILCallsPerHour}
                        onChange={(e) => setSettings({ ...settings, maxAILCallsPerHour: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 rounded-lg border border-zinc-300"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-2">
                        每小时最大工作流运行次数
                      </label>
                      <input
                        type="number"
                        value={settings.maxWorkflowRunsPerHour}
                        onChange={(e) => setSettings({ ...settings, maxWorkflowRunsPerHour: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 rounded-lg border border-zinc-300"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-2">
                        每 IP 最大注册次数
                      </label>
                      <input
                        type="number"
                        value={settings.maxRegistrationsPerIP}
                        onChange={(e) => setSettings({ ...settings, maxRegistrationsPerIP: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 rounded-lg border border-zinc-300"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">
                      黑名单 IP
                    </label>
                    <div className="flex gap-2 mb-4">
                      <input
                        type="text"
                        value={newIP}
                        onChange={(e) => setNewIP(e.target.value)}
                        placeholder="输入 IP 地址"
                        className="flex-1 px-3 py-2 rounded-lg border border-zinc-300"
                      />
                      <Button variant="secondary" onClick={handleAddBlockedIP}>
                        <Ban className="w-4 h-4" />
                        添加
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {settings.blockedIPs.map((ip) => (
                        <div key={ip} className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                          <code className="text-sm">{ip}</code>
                          <button
                            onClick={() => handleRemoveBlockedIP(ip)}
                            className="text-error-600 hover:text-error-700"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      {settings.blockedIPs.length === 0 && (
                        <p className="text-zinc-500 text-center py-4">暂无黑名单 IP</p>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button onClick={handleUpdateSettings}>
                      <CheckCircle className="w-4 h-4" />
                      保存设置
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
